'use client';
import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { createClient } from '@/lib/supabase/client';

// ─── Types ────────────────────────────────────────────────────────────────────
interface IntakeSubmission {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  businessName: string;
  businessType: string;
  services: string[];
  frequency: string;
  currentSoftware: string;
  monthsBehind: string;
  message: string;
  status: string;
  createdAt: string;
}

interface Proposal {
  id: string;
  status: 'pending' | 'sent' | 'accepted' | 'declined';
  sentAt: string | null;
  acceptedAt: string | null;
  declinedAt: string | null;
}

interface Contract {
  id: string;
  status: 'pending' | 'sent' | 'signed' | 'active';
  templateName: string | null;
  signatureLink: string | null;
  sentAt: string | null;
  signedAt: string | null;
  activatedAt: string | null;
}

interface ClientData {
  intake: IntakeSubmission | null;
  proposal: Proposal | null;
  contract: Contract | null;
}

const FREQ_LABELS: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  'one-time': 'One-time Project',
};

const BEHIND_LABELS: Record<string, string> = {
  current: 'Current',
  '1-3': '1–3 months behind',
  '4-6': '4–6 months behind',
  '7-12': '7–12 months behind',
  '12+': '12+ months behind',
};

// ─── Status Badge ─────────────────────────────────────────────────────────────
function StatusBadge({ status }: { status: string }) {
  const configs: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    pending:  { label: 'Pending Review',  bg: 'bg-white/5',           text: 'text-white/50',    dot: 'bg-white/30' },
    new:      { label: 'Under Review',    bg: 'bg-blue-500/10',       text: 'text-blue-400',    dot: 'bg-blue-400' },
    sent:     { label: 'Sent',            bg: 'bg-[#fd5108]/10',      text: 'text-[#fd5108]',   dot: 'bg-[#fd5108]' },
    accepted: { label: 'Accepted',        bg: 'bg-emerald-500/10',    text: 'text-emerald-400', dot: 'bg-emerald-400' },
    declined: { label: 'Declined',        bg: 'bg-red-500/10',        text: 'text-red-400',     dot: 'bg-red-400' },
    signed:   { label: 'Signed',          bg: 'bg-emerald-500/10',    text: 'text-emerald-400', dot: 'bg-emerald-400' },
    active:   { label: 'Active',          bg: 'bg-emerald-500/10',    text: 'text-emerald-400', dot: 'bg-emerald-400' },
    proposal_sent:       { label: 'Proposal Sent',       bg: 'bg-amber-500/10',   text: 'text-amber-400',   dot: 'bg-amber-400' },
    signature_requested: { label: 'Sig. Requested',      bg: 'bg-purple-500/10',  text: 'text-purple-400',  dot: 'bg-purple-400' },
    closed:   { label: 'Closed',          bg: 'bg-white/5',           text: 'text-white/30',    dot: 'bg-white/20' },
  };
  const cfg = configs[status] || configs['pending'];
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold tracking-wide ${cfg.bg} ${cfg.text}`}
      style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

// ─── Progress Timeline ────────────────────────────────────────────────────────
function ProgressTimeline({ intake, proposal, contract }: { intake: IntakeSubmission | null; proposal: Proposal | null; contract: Contract | null }) {
  const steps = [
    {
      label: 'Intake Received',
      done: !!intake,
      active: !!intake && !proposal,
      date: intake?.createdAt ? new Date(intake.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null,
    },
    {
      label: 'Proposal Sent',
      done: proposal?.status === 'sent' || proposal?.status === 'accepted',
      active: proposal?.status === 'sent',
      date: proposal?.sentAt ? new Date(proposal.sentAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null,
    },
    {
      label: 'Proposal Accepted',
      done: proposal?.status === 'accepted',
      active: false,
      date: proposal?.acceptedAt ? new Date(proposal.acceptedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null,
    },
    {
      label: 'Contract Signed',
      done: contract?.status === 'signed' || contract?.status === 'active',
      active: contract?.status === 'sent',
      date: contract?.signedAt ? new Date(contract.signedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null,
    },
    {
      label: 'Engagement Active',
      done: contract?.status === 'active',
      active: false,
      date: contract?.activatedAt ? new Date(contract.activatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : null,
    },
  ];

  return (
    <div className="flex items-start gap-0 overflow-x-auto pb-2">
      {steps.map((step, i) => (
        <div key={step.label} className="flex items-center flex-shrink-0">
          <div className="flex flex-col items-center gap-2 min-w-[100px] sm:min-w-[120px]">
            <div className={`w-8 h-8 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all duration-300 ${
              step.done
                ? 'bg-emerald-500 border-emerald-500'
                : step.active
                ? 'bg-[#fd5108]/20 border-[#fd5108]'
                : 'bg-white/5 border-white/15'
            }`}>
              {step.done ? (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M3 7l3 3 5-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              ) : step.active ? (
                <span className="w-2 h-2 rounded-full bg-[#fd5108]" />
              ) : (
                <span className="w-2 h-2 rounded-full bg-white/20" />
              )}
            </div>
            <div className="text-center">
              <p className={`text-xs font-semibold leading-tight ${step.done ? 'text-emerald-400' : step.active ? 'text-[#fd5108]' : 'text-white/30'}`}
                style={{ fontFamily: "'DM Sans', sans-serif" }}>
                {step.label}
              </p>
              {step.date && (
                <p className="text-white/25 text-xs mt-0.5" style={{ fontFamily: "'DM Sans', sans-serif" }}>{step.date}</p>
              )}
            </div>
          </div>
          {i < steps.length - 1 && (
            <div className={`h-0.5 w-8 sm:w-12 flex-shrink-0 mb-6 transition-all duration-300 ${
              steps[i + 1].done || steps[i + 1].active ? 'bg-[#fd5108]/40' : 'bg-white/10'
            }`} />
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function ClientPortalPage() {
  const [authMode, setAuthMode] = useState<'login' | 'signup' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [clientData, setClientData] = useState<ClientData>({ intake: null, proposal: null, contract: null });
  const [dataLoading, setDataLoading] = useState(false);
  const [dataError, setDataError] = useState('');

  const supabase = createClient();

  useEffect(() => {
    setMounted(true);
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  const fetchClientData = useCallback(async (userId: string, userEmail: string) => {
    setDataLoading(true);
    setDataError('');
    try {
      // Fetch intake submission
      const { data: intakes, error: intakeErr } = await supabase
        .from('intake_submissions')
        .select('*')
        .or(`user_id.eq.${userId},email.eq.${userEmail}`)
        .order('created_at', { ascending: false })
        .limit(1);

      if (intakeErr) {
        setDataError('Unable to load your data. Please try again.');
        setDataLoading(false);
        return;
      }

      const rawIntake = intakes?.[0] ?? null;
      if (!rawIntake) {
        setClientData({ intake: null, proposal: null, contract: null });
        setDataLoading(false);
        return;
      }

      const intake: IntakeSubmission = {
        id: rawIntake.id,
        firstName: rawIntake.first_name,
        lastName: rawIntake.last_name,
        email: rawIntake.email,
        phone: rawIntake.phone || '',
        businessName: rawIntake.business_name,
        businessType: rawIntake.business_type || '',
        services: rawIntake.services || [],
        frequency: rawIntake.frequency || '',
        currentSoftware: rawIntake.current_software || '',
        monthsBehind: rawIntake.months_behind || '',
        message: rawIntake.message || '',
        status: rawIntake.status || 'new',
        createdAt: rawIntake.created_at,
      };

      // Fetch proposal
      const { data: proposals } = await supabase
        .from('proposals')
        .select('*')
        .eq('intake_id', intake.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const rawProposal = proposals?.[0] ?? null;
      const proposal: Proposal | null = rawProposal
        ? {
            id: rawProposal.id,
            status: rawProposal.status,
            sentAt: rawProposal.sent_at,
            acceptedAt: rawProposal.accepted_at,
            declinedAt: rawProposal.declined_at,
          }
        : null;

      // Fetch contract
      const { data: contracts } = await supabase
        .from('contracts')
        .select('*')
        .eq('intake_id', intake.id)
        .order('created_at', { ascending: false })
        .limit(1);

      const rawContract = contracts?.[0] ?? null;
      const contract: Contract | null = rawContract
        ? {
            id: rawContract.id,
            status: rawContract.status,
            templateName: rawContract.template_name,
            signatureLink: rawContract.signature_link,
            sentAt: rawContract.sent_at,
            signedAt: rawContract.signed_at,
            activatedAt: rawContract.activated_at,
          }
        : null;

      setClientData({ intake, proposal, contract });
    } catch {
      setDataError('Something went wrong loading your data.');
    }
    setDataLoading(false);
  }, []);

  useEffect(() => {
    if (user) {
      fetchClientData(user.id, user.email);
    }
  }, [user, fetchClientData]);

  // ─── Real-time subscriptions ────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;

    // Subscribe to intake_submissions changes for this user
    const intakeChannel = supabase
      .channel('client-intake-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'intake_submissions',
          filter: `email=eq.${user.email}`,
        },
        () => {
          fetchClientData(user.id, user.email);
        }
      )
      .subscribe();

    // Subscribe to proposals changes — re-fetch all client data when any proposal changes
    const proposalsChannel = supabase
      .channel('client-proposals-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'proposals',
        },
        () => {
          fetchClientData(user.id, user.email);
        }
      )
      .subscribe();

    // Subscribe to contracts changes
    const contractsChannel = supabase
      .channel('client-contracts-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'contracts',
        },
        () => {
          fetchClientData(user.id, user.email);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(intakeChannel);
      supabase.removeChannel(proposalsChannel);
      supabase.removeChannel(contractsChannel);
    };
  }, [user, fetchClientData]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        setAuthError(error.message === 'Invalid login credentials' ?'Incorrect email or password. Please try again.'
          : error.message);
      }
    } catch {
      setAuthError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    if (password !== confirmPassword) {
      setAuthError('Passwords do not match.');
      return;
    }
    if (password.length < 8) {
      setAuthError('Password must be at least 8 characters.');
      return;
    }
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      if (error) {
        setAuthError(error.message);
      } else {
        setAuthSuccess('Account created! You can now sign in.');
        setAuthMode('login');
        setPassword('');
        setConfirmPassword('');
      }
    } catch {
      setAuthError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthError('');
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/client-portal`,
      });
      if (error) {
        setAuthError(error.message);
      } else {
        setAuthSuccess('Password reset link sent to your email.');
        setAuthMode('login');
      }
    } catch {
      setAuthError('Something went wrong. Please try again.');
    }
    setIsLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setClientData({ intake: null, proposal: null, contract: null });
    setEmail('');
    setPassword('');
  };

  if (!mounted) return null;

  return (
    <div className="min-h-screen bg-[#111111]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Top bar */}
      <div className="border-b border-white/8">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 h-14 sm:h-16 flex items-center justify-between">
          <Link href="/" className="flex items-center gap-2 group">
            <span className="font-bold text-white text-lg tracking-tight"
              style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.5px' }}>
              SYHJ
            </span>
            <span className="w-1.5 h-1.5 rounded-full bg-[#fd5108]" aria-hidden="true" />
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-white/30 text-xs tracking-widest uppercase hidden sm:block"
              style={{ letterSpacing: '2px' }}>
              Client Portal
            </span>
            {user && (
              <button
                onClick={handleLogout}
                className="text-white/40 hover:text-white/70 text-xs transition-colors duration-200 ml-2 flex items-center gap-1.5"
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M4 2H2a1 1 0 00-1 1v6a1 1 0 001 1h2M8 8.5L10.5 6 8 3.5M10.5 6H4.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                Sign out
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      {!user ? (
        <AuthView
          mode={authMode}
          email={email}
          password={password}
          confirmPassword={confirmPassword}
          fullName={fullName}
          error={authError}
          success={authSuccess}
          isLoading={isLoading}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onConfirmPasswordChange={setConfirmPassword}
          onFullNameChange={setFullName}
          onModeChange={(m) => { setAuthMode(m); setAuthError(''); setAuthSuccess(''); }}
          onLogin={handleLogin}
          onSignup={handleSignup}
          onForgotPassword={handleForgotPassword}
        />
      ) : (
        <DashboardView
          user={user}
          clientData={clientData}
          dataLoading={dataLoading}
          dataError={dataError}
          onLogout={handleLogout}
          onRefresh={() => fetchClientData(user.id, user.email)}
        />
      )}
    </div>
  );
}

// ─── Auth View ────────────────────────────────────────────────────────────────
interface AuthViewProps {
  mode: 'login' | 'signup' | 'forgot';
  email: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  error: string;
  success: string;
  isLoading: boolean;
  onEmailChange: (v: string) => void;
  onPasswordChange: (v: string) => void;
  onConfirmPasswordChange: (v: string) => void;
  onFullNameChange: (v: string) => void;
  onModeChange: (m: 'login' | 'signup' | 'forgot') => void;
  onLogin: (e: React.FormEvent) => void;
  onSignup: (e: React.FormEvent) => void;
  onForgotPassword: (e: React.FormEvent) => void;
}

function AuthView({
  mode, email, password, confirmPassword, fullName, error, success, isLoading,
  onEmailChange, onPasswordChange, onConfirmPasswordChange, onFullNameChange,
  onModeChange, onLogin, onSignup, onForgotPassword,
}: AuthViewProps) {
  const inputCls = "w-full bg-white/5 border border-white/10 text-white placeholder-white/20 px-4 py-3 text-sm focus:outline-none focus:border-[#fd5108] transition-colors duration-200";
  const labelCls = "block text-white/60 text-xs font-semibold tracking-widest uppercase mb-2";

  return (
    <div className="flex items-center justify-center min-h-[calc(100vh-64px)] px-4 py-16">
      <div className="w-full max-w-md">
        {/* Icon */}
        <div className="w-12 h-12 border border-white/10 flex items-center justify-center mb-8">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="3" y="8" width="14" height="10" rx="1" stroke="#fd5108" strokeWidth="1.5" />
            <path d="M7 8V6a3 3 0 016 0v2" stroke="#fd5108" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="10" cy="13" r="1.5" fill="#fd5108" />
          </svg>
        </div>

        <p className="text-[#fd5108] text-xs font-semibold tracking-widest uppercase mb-3" style={{ letterSpacing: '3px' }}>
          {mode === 'login' ? 'Secure Access' : mode === 'signup' ? 'Create Account' : 'Reset Password'}
        </p>
        <h1 className="text-white text-3xl font-bold mb-2"
          style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.5px' }}>
          {mode === 'login' ? 'Client Portal' : mode === 'signup' ? 'Get Started' : 'Forgot Password'}
        </h1>
        <p className="text-white/45 text-sm mb-10 leading-relaxed">
          {mode === 'login' ?'Sign in to track your intake status, view proposals, and manage contracts.'
            : mode === 'signup' ?'Create an account to access your client dashboard and track your engagement.' :'Enter your email and we\'ll send you a password reset link.'}
        </p>

        {success && (
          <div className="border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 mb-6">
            <p className="text-emerald-400 text-xs">{success}</p>
          </div>
        )}

        {/* Login Form */}
        {mode === 'login' && (
          <form onSubmit={onLogin} className="space-y-5">
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Email Address</label>
              <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
                placeholder="you@example.com" required className={inputCls} />
            </div>
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Password</label>
              <input type="password" value={password} onChange={(e) => onPasswordChange(e.target.value)}
                placeholder="Your password" required className={inputCls} />
            </div>
            {error && (
              <div className="border border-red-500/30 bg-red-500/5 px-4 py-3">
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}
            <button type="submit" disabled={isLoading}
              className="w-full bg-[#fd5108] text-white font-bold text-sm py-4 transition-all duration-300 hover:bg-[#e04507] disabled:opacity-50 disabled:cursor-not-allowed mt-2">
              {isLoading ? 'Signing in…' : 'Sign In'}
            </button>
            <div className="flex items-center justify-between pt-1">
              <button type="button" onClick={() => onModeChange('forgot')}
                className="text-white/30 hover:text-white/60 text-xs transition-colors duration-200 bg-transparent cursor-pointer">
                Forgot password?
              </button>
              <button type="button" onClick={() => onModeChange('signup')}
                className="text-[#fd5108]/70 hover:text-[#fd5108] text-xs transition-colors duration-200 bg-transparent cursor-pointer">
                Create account →
              </button>
            </div>
          </form>
        )}

        {/* Signup Form */}
        {mode === 'signup' && (
          <form onSubmit={onSignup} className="space-y-5">
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Full Name</label>
              <input type="text" value={fullName} onChange={(e) => onFullNameChange(e.target.value)}
                placeholder="Your full name" required className={inputCls} />
            </div>
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Email Address</label>
              <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
                placeholder="you@example.com" required className={inputCls} />
            </div>
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Password</label>
              <input type="password" value={password} onChange={(e) => onPasswordChange(e.target.value)}
                placeholder="Min. 8 characters" required minLength={8} className={inputCls} />
            </div>
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Confirm Password</label>
              <input type="password" value={confirmPassword} onChange={(e) => onConfirmPasswordChange(e.target.value)}
                placeholder="Repeat your password" required className={inputCls} />
            </div>
            {error && (
              <div className="border border-red-500/30 bg-red-500/5 px-4 py-3">
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}
            <button type="submit" disabled={isLoading}
              className="w-full bg-[#fd5108] text-white font-bold text-sm py-4 transition-all duration-300 hover:bg-[#e04507] disabled:opacity-50 disabled:cursor-not-allowed mt-2">
              {isLoading ? 'Creating account…' : 'Create Account'}
            </button>
            <div className="text-center pt-1">
              <button type="button" onClick={() => onModeChange('login')}
                className="text-white/30 hover:text-white/60 text-xs transition-colors duration-200 bg-transparent cursor-pointer">
                Already have an account? Sign in
              </button>
            </div>
          </form>
        )}

        {/* Forgot Password Form */}
        {mode === 'forgot' && (
          <form onSubmit={onForgotPassword} className="space-y-5">
            <div>
              <label className={labelCls} style={{ letterSpacing: '2px' }}>Email Address</label>
              <input type="email" value={email} onChange={(e) => onEmailChange(e.target.value)}
                placeholder="you@example.com" required className={inputCls} />
            </div>
            {error && (
              <div className="border border-red-500/30 bg-red-500/5 px-4 py-3">
                <p className="text-red-400 text-xs">{error}</p>
              </div>
            )}
            <button type="submit" disabled={isLoading}
              className="w-full bg-[#fd5108] text-white font-bold text-sm py-4 transition-all duration-300 hover:bg-[#e04507] disabled:opacity-50 disabled:cursor-not-allowed mt-2">
              {isLoading ? 'Sending…' : 'Send Reset Link'}
            </button>
            <div className="text-center pt-1">
              <button type="button" onClick={() => onModeChange('login')}
                className="text-white/30 hover:text-white/60 text-xs transition-colors duration-200 bg-transparent cursor-pointer">
                ← Back to sign in
              </button>
            </div>
          </form>
        )}

        <p className="text-white/20 text-xs text-center mt-10 leading-relaxed">
          Only clients who have submitted an intake form can access this portal.
          <br />
          Questions?{' '}
          <a href="mailto:hello@syhj.co" className="text-white/40 hover:text-white/60 transition-colors">
            Contact SYHJ
          </a>
        </p>
      </div>
    </div>
  );
}

// ─── Dashboard View ───────────────────────────────────────────────────────────
interface DashboardViewProps {
  user: any;
  clientData: ClientData;
  dataLoading: boolean;
  dataError: string;
  onLogout: () => void;
  onRefresh: () => void;
}

function DashboardView({ user, clientData, dataLoading, dataError, onLogout, onRefresh }: DashboardViewProps) {
  const { intake, proposal, contract } = clientData;
  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'Client';

  if (dataLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#fd5108]/30 border-t-[#fd5108] rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/30 text-sm">Loading your dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-12 py-10 sm:py-14">
      {/* Welcome header */}
      <div className="mb-10 sm:mb-12 flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="text-[#fd5108] text-xs font-semibold tracking-widest uppercase mb-2" style={{ letterSpacing: '3px' }}>
            Welcome back
          </p>
          <h2 className="text-white text-3xl sm:text-4xl font-bold mb-1"
            style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: '-0.5px' }}>
            {displayName}
          </h2>
          <p className="text-white/35 text-sm">{user?.email}</p>
        </div>
        <button onClick={onRefresh}
          className="flex items-center gap-2 text-white/30 hover:text-white/60 text-xs transition-colors duration-200 self-start sm:self-auto">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <path d="M10 6A4 4 0 112 6" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
            <path d="M10 3v3H7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Refresh
        </button>
      </div>

      {dataError && (
        <div className="border border-red-500/30 bg-red-500/5 px-4 py-3 mb-8">
          <p className="text-red-400 text-xs">{dataError}</p>
        </div>
      )}

      {!intake ? (
        /* No intake found */
        <div className="border border-white/8 bg-white/3 p-8 sm:p-12 text-center">
          <div className="w-14 h-14 border border-white/10 flex items-center justify-center mx-auto mb-6">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="4" y="3" width="16" height="18" rx="1" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
              <path d="M8 8h8M8 12h8M8 16h5" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </div>
          <h3 className="text-white text-xl font-bold mb-3"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            No intake found
          </h3>
          <p className="text-white/40 text-sm leading-relaxed mb-6 max-w-md mx-auto">
            We couldn&apos;t find an intake submission linked to your account ({user?.email}).
            If you&apos;ve submitted an intake form, please contact SYHJ to link it to your account.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link href="/#portal"
              className="bg-[#fd5108] text-white font-bold text-sm px-6 py-3 hover:bg-[#e04507] transition-colors duration-200">
              Submit Intake Form
            </Link>
            <a href="mailto:hello@syhj.co"
              className="text-white/40 hover:text-white/70 text-sm transition-colors duration-200">
              Contact SYHJ →
            </a>
          </div>
        </div>
      ) : (
        <>
          {/* Progress Timeline */}
          <section className="mb-10 sm:mb-12">
            <div className="flex items-center gap-4 mb-6">
              <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Engagement Progress
              </h3>
              <div className="flex-1 h-px bg-white/8" />
            </div>
            <div className="border border-white/8 bg-white/3 p-5 sm:p-6 overflow-x-auto">
              <ProgressTimeline intake={intake} proposal={proposal} contract={contract} />
            </div>
          </section>

          {/* Status Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10 sm:mb-12">
            {/* Intake */}
            <div className="border border-white/8 bg-white/3 p-5">
              <p className="text-white/30 text-xs font-semibold tracking-widest uppercase mb-3" style={{ letterSpacing: '2px' }}>
                Intake Status
              </p>
              <StatusBadge status={intake.status} />
              <p className="text-white/25 text-xs mt-2">
                Submitted {new Date(intake.createdAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>

            {/* Proposal */}
            <div className="border border-white/8 bg-white/3 p-5">
              <p className="text-white/30 text-xs font-semibold tracking-widest uppercase mb-3" style={{ letterSpacing: '2px' }}>
                Proposal
              </p>
              <StatusBadge status={proposal?.status || 'pending'} />
              <p className="text-white/25 text-xs mt-2">
                {proposal?.status === 'sent' ? 'Check your email for the proposal' :
                 proposal?.status === 'accepted' ? 'Proposal accepted' :
                 proposal?.status === 'declined' ? 'Proposal declined' : 'Being prepared by SYHJ'}
              </p>
            </div>

            {/* Contract */}
            <div className="border border-white/8 bg-white/3 p-5">
              <p className="text-white/30 text-xs font-semibold tracking-widest uppercase mb-3" style={{ letterSpacing: '2px' }}>
                Contract
              </p>
              <StatusBadge status={contract?.status || 'pending'} />
              <p className="text-white/25 text-xs mt-2">
                {contract?.status === 'sent' ? 'Awaiting your signature' :
                 contract?.status === 'signed' ? 'Contract signed' :
                 contract?.status === 'active' ? 'Engagement active' : 'Pending proposal acceptance'}
              </p>
            </div>
          </div>

          {/* Intake Summary */}
          <section className="mb-10 sm:mb-12">
            <div className="flex items-center gap-4 mb-6">
              <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Intake Summary
              </h3>
              <div className="flex-1 h-px bg-white/8" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
              {[
                { label: 'Business Name', value: intake.businessName },
                { label: 'Business Type', value: intake.businessType || '—' },
                { label: 'Contact Email', value: intake.email },
                { label: 'Phone', value: intake.phone || '—' },
                { label: 'Current Software', value: intake.currentSoftware || '—' },
                { label: 'Books Status', value: BEHIND_LABELS[intake.monthsBehind] || intake.monthsBehind || '—' },
              ].map((item) => (
                <div key={item.label} className="border-l-2 border-[#fd5108] pl-4 py-1">
                  <p className="text-white/30 text-xs font-semibold tracking-widest uppercase mb-1" style={{ letterSpacing: '1.5px' }}>
                    {item.label}
                  </p>
                  <p className="text-white text-sm font-medium">{item.value}</p>
                </div>
              ))}
            </div>
            <div>
              <p className="text-white/30 text-xs font-semibold tracking-widest uppercase mb-3" style={{ letterSpacing: '2px' }}>
                Requested Services
              </p>
              <div className="flex flex-wrap gap-2">
                {intake.services.map((svc) => (
                  <span key={svc} className="bg-[#fd5108]/10 text-[#fd5108] text-xs font-semibold px-3 py-1.5 border border-[#fd5108]/20">
                    {svc}
                  </span>
                ))}
                {intake.frequency && (
                  <span className="bg-white/5 text-white/50 text-xs font-semibold px-3 py-1.5 border border-white/10">
                    {FREQ_LABELS[intake.frequency] || intake.frequency}
                  </span>
                )}
              </div>
            </div>
            {intake.message && (
              <div className="mt-5 bg-white/3 border border-white/8 p-5">
                <p className="text-white/30 text-xs font-semibold tracking-widest uppercase mb-3" style={{ letterSpacing: '2px' }}>
                  Your Message
                </p>
                <p className="text-white/60 text-sm leading-relaxed italic">&ldquo;{intake.message}&rdquo;</p>
              </div>
            )}
          </section>

          {/* Proposal Section */}
          <section className="mb-10 sm:mb-12">
            <div className="flex items-center gap-4 mb-6">
              <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Proposal
              </h3>
              <div className="flex-1 h-px bg-white/8" />
              <StatusBadge status={proposal?.status || 'pending'} />
            </div>
            <div className="border border-white/8 bg-white/3 p-6 sm:p-8">
              {(!proposal || proposal.status === 'pending') && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <circle cx="9" cy="9" r="7" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
                      <path d="M9 5v4l2.5 2.5" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-semibold mb-1">Your proposal is being prepared</p>
                    <p className="text-white/35 text-sm leading-relaxed">
                      SYHJ is reviewing your intake and preparing a detailed proposal tailored to {intake.businessName}. You&apos;ll be notified at {intake.email} once it&apos;s ready.
                    </p>
                  </div>
                </div>
              )}
              {proposal?.status === 'sent' && (
                <div>
                  <p className="text-white/70 text-sm font-semibold mb-2">Your proposal has been sent</p>
                  <p className="text-white/35 text-sm leading-relaxed mb-4">
                    A detailed proposal for {intake.businessName} has been sent to {intake.email}. Please review and reply to accept or request changes.
                  </p>
                  <div className="border-t border-white/8 pt-4">
                    <p className="text-white/25 text-xs">
                      Haven&apos;t received it? Check your spam folder or{' '}
                      <a href="mailto:hello@syhj.co" className="text-[#fd5108] hover:underline">contact SYHJ</a>.
                    </p>
                  </div>
                </div>
              )}
              {proposal?.status === 'accepted' && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M4 9l4 4 6-6" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-emerald-400 text-sm font-semibold mb-1">Proposal accepted</p>
                    <p className="text-white/35 text-sm leading-relaxed">
                      You&apos;ve accepted the proposal for {intake.businessName}. A service contract is being prepared and will be sent to {intake.email} shortly.
                    </p>
                  </div>
                </div>
              )}
              {proposal?.status === 'declined' && (
                <div>
                  <p className="text-white/50 text-sm font-semibold mb-2">Proposal declined</p>
                  <p className="text-white/30 text-sm leading-relaxed">
                    The proposal was declined. If you&apos;d like to revisit or discuss adjustments, please{' '}
                    <a href="mailto:hello@syhj.co" className="text-[#fd5108] hover:underline">reach out to SYHJ</a>.
                  </p>
                </div>
              )}
            </div>
          </section>

          {/* Contract Section */}
          <section className="mb-10 sm:mb-12">
            <div className="flex items-center gap-4 mb-6">
              <h3 className="text-white text-lg font-bold" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                Contract &amp; Signature
              </h3>
              <div className="flex-1 h-px bg-white/8" />
              <StatusBadge status={contract?.status || 'pending'} />
            </div>
            <div className="border border-white/8 bg-white/3 p-6 sm:p-8">
              {(!contract || contract.status === 'pending') && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 border border-white/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <rect x="3" y="2" width="12" height="14" rx="1" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
                      <path d="M6 6h6M6 9h6M6 12h4" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" strokeLinecap="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-white/70 text-sm font-semibold mb-1">Contract not yet issued</p>
                    <p className="text-white/35 text-sm leading-relaxed">
                      A service agreement will be prepared once the proposal is accepted. It will be sent to {intake.email} for review and signature.
                    </p>
                  </div>
                </div>
              )}
              {contract?.status === 'sent' && (
                <div>
                  <p className="text-white/70 text-sm font-semibold mb-2">Contract sent — awaiting your signature</p>
                  {contract.templateName && (
                    <p className="text-white/50 text-xs mb-3">Agreement: {contract.templateName}</p>
                  )}
                  <p className="text-white/35 text-sm leading-relaxed mb-4">
                    A service agreement for {intake.businessName} has been sent to {intake.email}. Please review and sign to begin the engagement.
                  </p>
                  {contract.signatureLink && (
                    <a href={contract.signatureLink} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#fd5108] text-white font-bold text-sm px-5 py-3 hover:bg-[#e04507] transition-colors duration-200 mb-4">
                      <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                        <path d="M2 10.5c1-1 2-2 3-1.5s1.5 1.5 2.5.5 1-2 2-2.5" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M1 12.5h12" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
                        <path d="M9 2l2 2-5 5H4V7l5-5z" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      Sign Agreement
                    </a>
                  )}
                  <div className="border-t border-white/8 pt-4">
                    <p className="text-white/25 text-xs">
                      Questions about the contract?{' '}
                      <a href="mailto:hello@syhj.co" className="text-[#fd5108] hover:underline">Contact SYHJ</a>
                    </p>
                  </div>
                </div>
              )}
              {contract?.status === 'signed' && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <path d="M4 9l4 4 6-6" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-emerald-400 text-sm font-semibold mb-1">Contract signed</p>
                    <p className="text-white/35 text-sm leading-relaxed">
                      Your service agreement is signed. SYHJ will begin work on {intake.businessName}&apos;s engagement within 3–5 business days.
                    </p>
                  </div>
                </div>
              )}
              {contract?.status === 'active' && (
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                      <circle cx="9" cy="9" r="7" stroke="#34d399" strokeWidth="1.5" />
                      <path d="M6 9l2 2 4-4" stroke="#34d399" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-emerald-400 text-sm font-semibold mb-1">Engagement active</p>
                    <p className="text-white/35 text-sm leading-relaxed">
                      Your engagement with SYHJ is active. Services are being delivered per the agreed scope and schedule.
                    </p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* Footer note */}
          <div className="border-t border-white/8 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <p className="text-white/20 text-xs leading-relaxed">
              This portal reflects the current status of your engagement with SYHJ.
              <br />
              For questions, email{' '}
              <a href="mailto:hello@syhj.co" className="text-white/40 hover:text-white/60 transition-colors">
                hello@syhj.co
              </a>
            </p>
            <button onClick={onLogout}
              className="text-white/25 hover:text-white/50 text-xs transition-colors duration-200 flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M5 2H2a1 1 0 00-1 1v8a1 1 0 001 1h3M9 10l3-3-3-3M12 7H5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}
